import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { assetsConst } from 'src/app/core/const/assets.const';
import { responseStatus } from 'src/app/core/const/responseStatus.const';
import { extractTimeFromISOString } from 'src/app/core/helpers/extractTimeFromISOString';
import { utcToLocalConversion } from 'src/app/core/helpers/utcToLocaleConversion.helper';

import { AuthService } from 'src/app/core/services/auth.service';
import { SocketService } from 'src/app/core/services/socket.service';
import { SpinnerService } from 'src/app/core/services/spinner.service';
import { ToasterService } from 'src/app/core/services/toastr.service';
import { UserData } from 'src/app/models/loginResponse';
import { logoutResponse } from 'src/app/models/logoutResponse';
import { environment } from 'src/environments/environment';
import {
  AppLanguage,
  LanguageService,
  SUPPORTED_LANGUAGES
} from 'src/app/core/services/language.service';

@Component({
  selector: 'mega-store-factory-after-login',
  templateUrl: './after-login.component.html',
  styleUrls: ['./after-login.component.scss']
})
export class AfterLoginComponent implements OnDestroy, OnInit {
  // Language
  languages = SUPPORTED_LANGUAGES;
  currentLanguage: AppLanguage = 'en';
  assetsConst = assetsConst;
  toggleValue = false;
  sidenavWidth = 18;
  destroy$: Subject<boolean> = new Subject<boolean>();
  image: string | null = '';
  subscriptionSubject$!: Subscription;
  src = 'assets/sounds/notification-sound1.mp3';
  audio = new Audio();

  constructor(
    private _languageService: LanguageService,
    public _authService: AuthService,
    private router: Router,
    private _spinnerService: SpinnerService,
    private _toasterService: ToasterService,
    private _translateService: TranslateService,
    private socketService: SocketService
  ) {
    this._languageService.language$
      .pipe(takeUntil(this.destroy$))
      .subscribe((language) => (this.currentLanguage = language));
    this.socketService.initSocket();
  }

  changeLanguage(language: AppLanguage): void {
    this._languageService.switchTo(language);
  }

  ngOnInit(): void {
    this.subscriptionSubject$ = this._authService.profileDetails
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: UserData | null) => {
        if (res) {
          this.image = res.image;
        } else {
          this.getProfileData();
        }
      });
    this.handleListenersAndEmitters();
  }

  handleListenersAndEmitters(): void {
    this.socketService
      .onEvent('newStockAssignedOk')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.playAudio();
          this._toasterService.successToastr(
            this._translateService.instant('newInventroyArrriver')
          );
        }
      });
  }

  playAudio(): void {
    this.audio.src = this.src;
    this.audio.load();
    this.audio.play();
    this.audio.muted = false;
  }

  getProfileData(): void {
    this._authService
      .getProfile()
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: UserData) => {
        this.image = res.image;
        res.openingTime = extractTimeFromISOString(
          utcToLocalConversion(res.openingTime)
        );
        res.closingTime = extractTimeFromISOString(
          utcToLocalConversion(res.closingTime)
        );
        this._authService.profileDetails.next(res);
      });
  }

  toggleSidenav(): void {
    this._authService.toggle();
  }

  logout(): void {
    this._authService.logoutAlert().then(res => {
      if (
        Object.prototype.hasOwnProperty.call(res, 'value') &&
        res.isConfirmed === true
      ) {
        this._spinnerService.addToLoader('logout');
        this._authService
          .logout()
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (res: logoutResponse) => {
              if (res.statusCode === responseStatus.SUCCESS) {
                localStorage.removeItem(environment.storageKey);
                this._spinnerService.removeFromLoader('logout');
                this._toasterService.successToastr(res.message);
                this.router.navigate(['/auth/login']);
              }
            },
            error: () => {
              this._spinnerService.removeFromLoader('logout');
            }
          });
      }
    });
  }

  toggleOnlineOffline(): void {
    this.socketService.emitAction('toggleOnlineOffline', {
      isOnline: this.toggleValue
    });
  }

  ngOnDestroy(): void {
    this.destroy$.complete();
    this.destroy$.unsubscribe();
    this.subscriptionSubject$.unsubscribe();
  }
}
